"use client";

import { useEffect, useRef, useState } from "react";
import { useInView, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

type TextScrambleTag = "h1" | "h2" | "h3" | "p" | "span" | "div";

const DEFAULT_CHARSET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

interface TextScrambleProps {
  text: string;
  as?: TextScrambleTag;
  className?: string;
  delay?: number;
  /** Total scramble duration in seconds. */
  duration?: number;
  charset?: string;
  /** Animate when scrolled into view, or on mount. */
  trigger?: "mount" | "inView";
  once?: boolean;
}

function scrambleFrame(
  target: string,
  progress: number,
  charset: string
): string {
  const chars = Array.from(target);
  const nonSpaceCount = chars.filter((c) => c !== " ").length || 1;
  let nonSpaceIndex = 0;

  return chars
    .map((char) => {
      if (char === " ") return " ";

      const settleThreshold = (nonSpaceIndex + 1) / nonSpaceCount;
      nonSpaceIndex += 1;

      if (progress >= settleThreshold) return char;

      return charset[Math.floor(Math.random() * charset.length)] ?? char;
    })
    .join("");
}

export function TextScramble({
  text,
  as: Tag = "span",
  className,
  delay = 0,
  duration = 0.75,
  charset = DEFAULT_CHARSET,
  trigger = "inView",
  once = true,
}: TextScrambleProps) {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once, amount: 0.5 });
  const [display, setDisplay] = useState(text);
  const animatedForText = useRef<string | null>(null);

  useEffect(() => {
    if (reduced) {
      setDisplay(text);
      animatedForText.current = text;
      return;
    }

    const ready = trigger === "mount" || isInView;
    if (!ready) return;
    if (once && animatedForText.current === text) return;

    let cancelled = false;
    let completed = false;
    let raf = 0;
    let startTime: number | null = null;
    const delayMs = delay * 1000;
    const durationMs = Math.max(duration * 1000, 1);

    animatedForText.current = text;
    setDisplay(
      Array.from(text)
        .map((c) =>
          c === " "
            ? " "
            : charset[Math.floor(Math.random() * charset.length)] ?? c
        )
        .join("")
    );

    const tick = (now: number) => {
      if (cancelled) return;
      if (startTime === null) startTime = now + delayMs;
      const elapsed = now - startTime;

      if (elapsed < 0) {
        raf = requestAnimationFrame(tick);
        return;
      }

      const progress = Math.min(1, elapsed / durationMs);
      if (progress >= 1) {
        setDisplay(text);
        completed = true;
        return;
      }

      setDisplay(scrambleFrame(text, progress, charset));
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      // Allow re-run after Strict Mode cleanup if we never finished.
      if (!completed) {
        animatedForText.current = null;
      }
    };
  }, [charset, delay, duration, isInView, once, reduced, text, trigger]);

  return (
    <Tag ref={ref as never} className={cn(className)} aria-label={text}>
      <span aria-hidden>{display}</span>
    </Tag>
  );
}
